
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Filter, X } from "lucide-react";

interface SearchFilters {
  location: string;
  cities: string;
  states: string;
  zipCodes: string;
  minPrice: string;
  maxPrice: string;
  bedrooms: string;
  bathrooms: string;
  propertyType: string;
  minArv: string;
  maxArv: string;
  status: string;
}

interface PropertySearchFiltersProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

const PropertySearchFilters = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters
}: PropertySearchFiltersProps) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Format number with commas for display
  const formatNumberWithCommas = (value: string): string => {
    const numericValue = value.replace(/[^0-9]/g, '');
    if (!numericValue) return '';
    return parseInt(numericValue, 10).toLocaleString();
  };

  // Parse formatted number back to raw numeric string
  const parseFormattedNumber = (value: string): string => {
    return value.replace(/[^0-9]/g, '');
  };

  const updateFilter = (key: keyof SearchFilters, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = () => {
    return (
      filters.cities.trim() !== '' ||
      filters.states.trim() !== '' ||
      filters.zipCodes.trim() !== '' ||
      Object.entries(filters).some(([key, value]) => {
        if (['location', 'cities', 'states', 'zipCodes'].includes(key)) return false;
        if (key === 'status') return value !== '' && value !== 'active';
        return value.trim() !== '' && value !== 'any';
      })
    );
  };

  const getActiveFilterCount = () => {
    let count = 0;
    
    // Count location-based filters
    if (filters.cities.trim() !== '') count++;
    if (filters.states.trim() !== '') count++;
    if (filters.zipCodes.trim() !== '') count++;
    
    // Count price/ARV filters
    if (filters.minPrice !== '') count++;
    if (filters.maxPrice !== '') count++;
    if (filters.minArv !== '') count++;
    if (filters.maxArv !== '') count++;
    
    // Count dropdown filters (excluding default values)
    if (filters.bedrooms !== '' && filters.bedrooms !== 'any') count++;
    if (filters.bathrooms !== '' && filters.bathrooms !== 'any') count++;
    if (filters.propertyType !== '' && filters.propertyType !== 'any') count++;
    if (filters.status !== '' && filters.status !== 'active') count++;
    
    return count;
  };

  const handleApplyFilters = () => {
    onApplyFilters();
    setIsFiltersOpen(false);
  };

  return (
    <Popover open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="relative" data-tour="search-filters">
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters() && (
            <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
              {getActiveFilterCount()}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="max-h-[500px] overflow-y-auto">
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Filter Properties</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFiltersOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Location Filters Section */}
            <div className="space-y-3 pb-4 border-b">
              <Label className="font-medium">Location Filters</Label>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Cities</Label>
                  <Input
                    placeholder="e.g., Detroit, Royal Oak, Southfield"
                    value={filters.cities}
                    onChange={(e) => updateFilter('cities', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">States</Label>
                  <Input
                    placeholder="e.g., MI, OH, FL"
                    value={filters.states}
                    onChange={(e) => updateFilter('states', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Zip Codes</Label>
                  <Input
                    placeholder="e.g., 48239, 48235, 48227"
                    value={filters.zipCodes}
                    onChange={(e) => updateFilter('zipCodes', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Price & ARV Filters */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price Range</Label>
                <div className="space-y-2">
                  <Input
                    placeholder="Min Price"
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithCommas(filters.minPrice)}
                    onChange={(e) => updateFilter('minPrice', parseFormattedNumber(e.target.value))}
                  />
                  <Input
                    placeholder="Max Price"
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithCommas(filters.maxPrice)}
                    onChange={(e) => updateFilter('maxPrice', parseFormattedNumber(e.target.value))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>ARV Range</Label>
                <div className="space-y-2">
                  <Input
                    placeholder="Min ARV"
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithCommas(filters.minArv)}
                    onChange={(e) => updateFilter('minArv', parseFormattedNumber(e.target.value))}
                  />
                  <Input
                    placeholder="Max ARV"
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithCommas(filters.maxArv)}
                    onChange={(e) => updateFilter('maxArv', parseFormattedNumber(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* Property Details Filters */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bedrooms</Label>
                <Select value={filters.bedrooms} onValueChange={(value) => updateFilter('bedrooms', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                    <SelectItem value="5">5+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Bathrooms</Label>
                <Select value={filters.bathrooms} onValueChange={(value) => updateFilter('bathrooms', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="1.5">1.5+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="2.5">2.5+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Property Type</Label>
                <Select value={filters.propertyType} onValueChange={(value) => updateFilter('propertyType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="Single Family">Single Family</SelectItem>
                    <SelectItem value="Multi Family">Multi Family</SelectItem>
                    <SelectItem value="Multi Unit">Multi Unit</SelectItem>
                    <SelectItem value="Townhouse">Townhouse</SelectItem>
                    <SelectItem value="Condo">Condo</SelectItem>
                    <SelectItem value="Land">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={filters.status} onValueChange={(value) => updateFilter('status', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Active" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={onClearFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PropertySearchFilters;
